import { useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';
import { get, post, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

type Rules = Record<string, unknown>;
type Payload = { success: boolean; rules: Rules; isDefault?: boolean };

// Curated fields to render as number inputs, grouped. Any rules field NOT listed
// here is still preserved on save (we POST the whole draft) — this list only
// controls what's editable, never what's persisted.
const NUMBER_GROUPS: { group: string; title: string; fields: { key: string; label: string; step: number }[] }[] = [
  { group: 'decoration', title: 'Decoration Costs', fields: [
    { key: 'lcTransfer', label: 'LC Transfer', step: 0.05 },
    { key: 'ffTransfer', label: 'FF Transfer', step: 0.25 },
    { key: 'lcPress', label: 'LC/LL Press', step: 0.25 },
    { key: 'ffPress', label: 'FF/FC Press', step: 0.25 },
  ]},
  { group: 'perItem', title: 'Per-Item Costs', fields: [
    { key: 'rebagging', label: 'Rebagging', step: 0.05 },
    { key: 'heatPressBoxRemoval', label: 'Box Removal', step: 0.05 },
    { key: 'ghostfitWarehousing', label: 'GF Warehousing', step: 0.05 },
    { key: 'ecommerceHandling', label: 'Ecommerce Handling', step: 0.25 },
    { key: 'nonGhostfitBlankMarkup', label: 'Non-GF Blank Markup', step: 0.01 },
  ]},
  { group: 'perOrder', title: 'Per-Order Costs', fields: [
    { key: 'avgItemsPerOrder', label: 'Avg Items/Order', step: 0.5 },
    { key: 'creditCardPercent', label: 'CC %', step: 0.001 },
    { key: 'creditCardFlat', label: 'CC Flat ($/order)', step: 0.05 },
    { key: 'boxCharge', label: 'Box Charge ($/order)', step: 0.1 },
    { key: 'avgShippingCost', label: 'Avg Ship Cost', step: 1 },
    { key: 'customerShippingCharge', label: 'Customer Pays', step: 1 },
  ]},
  { group: 'commissions', title: 'Commissions', fields: [
    { key: 'salesRep', label: 'Sales Rep %', step: 0.01 },
    { key: 'team', label: 'Team %', step: 0.01 },
  ]},
];

const SCALAR_FIELDS: { key: string; label: string; step: number }[] = [
  { key: 'minimumProfit', label: 'Min Profit/Item ($)', step: 1 },
  { key: 'roundTo', label: 'Round To ($)', step: 1 },
];

function num(v: unknown): number {
  return typeof v === 'number' ? v : Number(v) || 0;
}

export default function Pricing() {
  const { data, isLoading } = useSWR<Payload>('/api/pricing-rules', get);
  const [draft, setDraft] = useState<Rules | null>(null);
  const [saving, setSaving] = useState(false);

  // Seed the draft from the COMPLETE loaded object. Edits mutate a deep clone;
  // every unrendered field rides along untouched until save.
  useEffect(() => {
    if (data?.rules) setDraft(JSON.parse(JSON.stringify(data.rules)));
  }, [data]);

  function setGroupField(group: string, key: string, value: string) {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as Rules;
      const g = (next[group] && typeof next[group] === 'object' ? next[group] : {}) as Record<string, unknown>;
      g[key] = Number(value) || 0;
      next[group] = g;
      return next;
    });
  }

  function setScalar(key: string, value: string) {
    setDraft((prev) => (prev ? { ...prev, [key]: Number(value) || 0 } : prev));
  }

  function setRoundDirection(value: string) {
    setDraft((prev) => (prev ? { ...prev, roundDirection: value } : prev));
  }

  // Brand minimum-retail multipliers: rules.brands = { "Nike": { minRetailMultiplier: 2.5 }, … }
  const brands = (draft?.brands && typeof draft.brands === 'object' ? draft.brands : {}) as Record<string, { minRetailMultiplier?: number }>;
  const [newBrand, setNewBrand] = useState('');

  function setBrandMultiplier(name: string, value: string) {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as Rules;
      const b = (next.brands && typeof next.brands === 'object' ? next.brands : {}) as Record<string, { minRetailMultiplier?: number }>;
      b[name] = { ...(b[name] || {}), minRetailMultiplier: Number(value) || 0 };
      next.brands = b;
      return next;
    });
  }

  function removeBrand(name: string) {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as Rules;
      const b = (next.brands && typeof next.brands === 'object' ? next.brands : {}) as Record<string, unknown>;
      delete b[name];
      next.brands = b;
      return next;
    });
  }

  function addBrand() {
    const name = newBrand.trim();
    if (!name) return;
    setBrandMultiplier(name, '2');
    setNewBrand('');
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    try {
      await post('/api/pricing-rules', draft);
      await mutate('/api/pricing-rules'); // revalidate (spec: SWR revalidate) — clears the isDefault banner once the doc exists
      toast.success('Pricing rules saved');
    } catch (e) {
      const body = (e as ApiError).body as { message?: string; error?: string } | null;
      toast.error(body?.message || body?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  function groupVal(group: string, key: string): number {
    const g = draft?.[group] as Record<string, unknown> | undefined;
    return num(g?.[key]);
  }

  const inputCls = 'w-full rounded-md border border-border bg-bg px-2 py-1.5 text-[12px]';

  return (
    <div className="px-7 py-6">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tighter">Pricing Rules</h1>
          <p className="text-[12px] text-muted">Global cost model driving calculated retail prices.</p>
        </div>
        <Button size="sm" disabled={!draft || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
      </div>

      {data?.isDefault && (
        <div className="mb-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[11px] text-warning">
          Editing default rules — saving creates the live pricing-rules document for the first time.
        </div>
      )}

      {isLoading || !draft ? (
        <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
      ) : (
        <div className="space-y-5">
          {NUMBER_GROUPS.map((g) => (
            <section key={g.group}>
              <h2 className="mb-2 text-[13px] font-semibold">{g.title}</h2>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {g.fields.map((f) => (
                  <label key={f.key} className="block">
                    <span className="mb-1 block text-[11px] text-muted">{f.label}</span>
                    <input
                      className={inputCls}
                      type="number"
                      step={f.step}
                      value={groupVal(g.group, f.key)}
                      onChange={(e) => setGroupField(g.group, f.key, e.target.value)}
                    />
                  </label>
                ))}
              </div>
            </section>
          ))}

          <section>
            <h2 className="mb-2 text-[13px] font-semibold">Targets</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {SCALAR_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="mb-1 block text-[11px] text-muted">{f.label}</span>
                  <input
                    className={inputCls}
                    type="number"
                    step={f.step}
                    value={num(draft[f.key])}
                    onChange={(e) => setScalar(f.key, e.target.value)}
                  />
                </label>
              ))}
              <label className="block">
                <span className="mb-1 block text-[11px] text-muted">Round Direction</span>
                <select
                  className={inputCls}
                  value={typeof draft.roundDirection === 'string' ? draft.roundDirection : 'up'}
                  onChange={(e) => setRoundDirection(e.target.value)}
                >
                  <option value="up">Up</option>
                  <option value="down">Down</option>
                  <option value="nearest">Nearest</option>
                </select>
              </label>
            </div>
          </section>

          <section>
            <h2 className="mb-1 text-[13px] font-semibold">Brand Minimum-Retail Multipliers</h2>
            <p className="mb-2 text-[11px] text-muted">
              Floor for calculated retail: blank cost × multiplier. Brands not listed use the cost model alone.
            </p>
            <div className="max-w-[460px] space-y-1.5">
              {Object.entries(brands).sort(([a], [b]) => a.localeCompare(b)).map(([name, v]) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="flex-1 truncate rounded-md border border-border bg-surface px-2 py-1.5 text-[12px]">{name}</span>
                  <input
                    className="w-24 rounded-md border border-border bg-bg px-2 py-1.5 text-[12px]"
                    type="number" step="0.1" min="0"
                    value={num(v?.minRetailMultiplier)}
                    onChange={(e) => setBrandMultiplier(name, e.target.value)}
                  />
                  <span className="text-[11px] text-muted">×</span>
                  <button
                    className="rounded-md px-2 py-1.5 text-[11px] text-danger transition-colors hover:bg-danger/[0.07]"
                    onClick={() => removeBrand(name)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <input
                  className="flex-1 rounded-md border border-border bg-bg px-2 py-1.5 text-[12px]"
                  placeholder="Add brand (e.g. Nike)"
                  value={newBrand}
                  onChange={(e) => setNewBrand(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addBrand()}
                />
                <Button size="sm" variant="secondary" onClick={addBrand} disabled={!newBrand.trim()}>Add brand</Button>
              </div>
            </div>
          </section>

          <p className="text-[11px] text-muted">
            Any rule fields not shown here are preserved untouched on save.
          </p>
        </div>
      )}
    </div>
  );
}
